/**
 * Terminal-state banner for a planning run that failed or stopped responding,
 * and the gentler notice for a slow step the server still reports as running.
 *
 * Without this the only signal a run died was the progress tracker never
 * finishing, which reads as "still working" for as long as the tab stays open.
 */
import React, { useRef, useState } from 'react';

import type { PlanningRunNoticeKind } from '../../hooks/usePlanningRunFailure';
import { normalizeApiError } from '../../services/apiError';
import { chatService } from '../../services/chatService';
import type { SupervisorButton } from '../../types/supervisorButtons';
import { recordUserActionClick } from '../AiOverviewPanel/useActionBarClicks';
import { PlanningActionTasks } from '../PlanningActionTasks';

const RETRY_BUTTON: SupervisorButton = {
  key: 'continue', kind: 'workflow', action: 'resume_design', label: 'Retry',
  payload: { phase: 'auto' },
};

/** The server's 422 detail when it has nothing it can replay right now. */
const RETRY_UNAVAILABLE_DETAIL = 'Action button is not currently available';
const CONNECTION_FAILURE = 'Retry could not be started. Check your connection and try again.';
const NOTHING_TO_RETRY = 'There is nothing to retry right now: the latest planning run is still going '
  + 'or has already finished. Refresh to see where it stands.';

interface PlanningFailureBannerProps {
  projectId: string | null;
  sessionId?: string | null;
  message: string | null;
  kind?: PlanningRunNoticeKind | null;
  onDismiss: () => void;
  /** Called when the server refuses a stall Retry because the run is still active. */
  onStillWorking?: () => void;
}

/**
 * The server refuses a source-less Retry with this 422 while the latest run is
 * still running (or has nothing failed to replay). It is not a connection fault.
 */
export const isRetryUnavailable = (error: unknown): boolean => {
  const normalized = normalizeApiError(error);

  return (normalized.status === 422 || normalized.code === 'VALIDATION_FAILED')
    && normalized.message === RETRY_UNAVAILABLE_DETAIL;
};

const startRetry = async (projectId: string, sessionId?: string | null): Promise<void> => {
  // This banner has no timeline entry; the server resolves the current
  // failed run and validates this source-less retry before creating work.
  const planningActionInvocationId = await recordUserActionClick(
    undefined, 'auto', 'continue', RETRY_BUTTON.label, projectId, sessionId, { required: true },
  );

  if (!planningActionInvocationId) throw new Error('The retry task could not be prepared.');
  // 'auto' lets the backend pick the phase it can safely replay rather than
  // restarting planning from the top and discarding finished pages.
  await chatService.resumeDesign(projectId, 'auto', sessionId, planningActionInvocationId);
};

/** The copy for a Retry that did not start, or null once a still-running run went back to the watchdog. */
const settleRetryFailure = (error: unknown, props: PlanningFailureBannerProps): string | null => {
  if (!isRetryUnavailable(error)) return CONNECTION_FAILURE;
  // After a stall, the refusal means the server still has the run going: say
  // so (the hook re-arms its watchdog) rather than leave a dead Retry.
  if (props.kind !== 'stalled' || !props.onStillWorking) return NOTHING_TO_RETRY;
  props.onStillWorking();

  return null;
};

const usePlanningRetry = (props: PlanningFailureBannerProps) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const retryPending = useRef(false);

  const runRetry = async (id: string) => {
    try {
      await startRetry(id, props.sessionId);
      props.onDismiss();
    } catch (error) {
      setRetryError(settleRetryFailure(error, props));
    } finally {
      retryPending.current = false;
      setIsRetrying(false);
    }
  };

  const handleRetry = () => {
    if (!props.projectId || retryPending.current) return;
    retryPending.current = true;
    setIsRetrying(true);
    setRetryError(null);
    void runRetry(props.projectId);
  };

  return { isRetrying, retryError, handleRetry };
};

/** Not a failure: no Retry (the server would refuse it), and Stop stays in the composer. */
const SlowStepNotice: React.FC<{ message: string; onDismiss: () => void }> = ({ message, onDismiss }) => (
  <div className="planning-error planning-error--waiting" role="status">
    <span>{message}</span>
    <button className="planning-error__retry-btn" onClick={onDismiss} type="button">
      Dismiss
    </button>
  </div>
);

export const PlanningFailureBanner: React.FC<PlanningFailureBannerProps> = (props) => {
  const { isRetrying, retryError, handleRetry } = usePlanningRetry(props);

  if (!props.message) return null;
  if (props.kind === 'slow') return <SlowStepNotice message={props.message} onDismiss={props.onDismiss} />;

  return (
    <>
      <PlanningActionTasks projectId={props.projectId} phase="auto" buttons={[RETRY_BUTTON]} current />
      <div className="planning-error" role="alert">
        <span>{retryError ?? props.message}</span>
        <button
          className="planning-error__retry-btn"
          onClick={handleRetry}
          disabled={isRetrying || !props.projectId}
          type="button"
        >
          {isRetrying ? 'Retrying…' : 'Retry'}
        </button>
        <button
          className="planning-error__retry-btn"
          onClick={props.onDismiss}
          type="button"
        >
          Dismiss
        </button>
      </div>
    </>
  );
};
